'use client'

import type { InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import type { InteractiveVideoPlayerApi } from '../InteractiveVideoPlayer'
import InfoCTAButton from '../modal/video-info-panel/InfoCTAButton'
import { BranchButton } from '../wide/WideBranchOverlay'
import { ChevronDown } from 'lucide-react'
import { FC, RefObject } from 'react'
import BoxChoicePill from './BoxChoicePill'

export type BoxBottomBarProps = {
  title: string
  config: PlayerConfig
  /** 動画に重ねる選択肢（分岐ボタン） */
  overlayButtons: InteractiveButton[]
  /** 現在ノードの選択肢。件数だけを見る（空なら最終ノード扱いで CV を出す） */
  choiceButtons: readonly unknown[]
  branchQuestion: string
  isChoiceOpen: boolean
  isDecisionVisible: boolean
  isSpCollapseHovered: boolean
  ctaUrl: string | null
  ctaLabel: string | null
  playerApiRef: RefObject<InteractiveVideoPlayerApi | null>
  onOpenDetail: () => void
  onOpenChoice: () => void
  onCollapseChoice: () => void
  onCollapseHoverChange: (hovered: boolean) => void
  onCtaClick?: () => void
  /** CVで別タブを開く直前に動画を止める */
  onPauseVideo?: () => void
}

/**
 * BOX型の狭い画面（1カラム）で動画の下に置く操作帯。
 *
 * 既存のSP動画モーダルと同じく、通常時は動画を大きく見せ、操作を下部へ集約する。
 * 詳細パネルを開いているあいだは出さない（中身が全て隠れ、余白だけの黒帯が残るため）。
 */
const BoxBottomBar: FC<BoxBottomBarProps> = ({
  title,
  config,
  overlayButtons,
  choiceButtons,
  branchQuestion,
  isChoiceOpen,
  isDecisionVisible,
  isSpCollapseHovered,
  ctaUrl,
  ctaLabel,
  playerApiRef,
  onOpenDetail,
  onOpenChoice,
  onCollapseChoice,
  onCollapseHoverChange,
  onCtaClick,
  onPauseVideo,
}) => (
        <div
          style={{
            flexShrink: 0,
            // 選択肢を開いているときも下端に余白を残す。セーフエリアだけに任せると
            // ノッチの無い端末（iPhone SE など）で 0 になり、最後のボタンが画面下端に貼り付く。
            padding: isChoiceOpen
              ? '8px 12px calc(8px + env(safe-area-inset-bottom, 0px))'
              : '8px 12px calc(10px + env(safe-area-inset-bottom, 0px))',
            background: '#1a1a1a',
          }}
        >
          {!isChoiceOpen && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: choiceButtons.length > 0 ? '9px' : 0,
                color: '#fff',
              }}
            >
              {overlayButtons.length > 0 ? (
                <BoxChoicePill
                  count={overlayButtons.length}
                  placement="inline"
                  onClick={onOpenChoice}
                />
              ) : (
                <span
                  style={{
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    fontSize: '14px',
                    fontWeight: 700,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </span>
              )}
              <button
                type="button"
                onClick={onOpenDetail}
                style={{
                  flexShrink: 0,
                  padding: '6px 16px',
                  border: '1px solid #fff',
                  borderRadius: '999px',
                  background: 'rgba(0,0,0,0.3)',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                詳しく見る
              </button>
            </div>
          )}

          {isChoiceOpen && overlayButtons.length > 0 && (
            <div data-testid="box-sp-choices" style={{ display: 'grid', gap: '7px' }}>
              <div
                style={{
                  display: 'flex',
                  width: 'fit-content',
                  minHeight: '32px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  justifySelf: 'center',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 780,
                  textAlign: 'center',
                }}
              >
                <span>{branchQuestion}</span>
                <button
                  type="button"
                  onClick={onCollapseChoice}
                  onMouseEnter={() => onCollapseHoverChange(true)}
                  onMouseLeave={() => onCollapseHoverChange(false)}
                  onFocus={() => onCollapseHoverChange(true)}
                  onBlur={() => onCollapseHoverChange(false)}
                  aria-label="選択肢を閉じて動画に戻る"
                  style={{
                    display: 'grid',
                    flexShrink: 0,
                    width: '32px',
                    height: '32px',
                    placeItems: 'center',
                    padding: 0,
                    border: isSpCollapseHovered
                      ? '1px solid rgba(255,255,255,0.5)'
                      : '1px solid rgba(255,255,255,0.28)',
                    borderRadius: '8px',
                    background: isSpCollapseHovered ? 'rgba(107,114,128,0.72)' : 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'background-color 160ms ease, border-color 160ms ease',
                  }}
                >
                  <ChevronDown size={18} strokeWidth={2.5} />
                </button>
              </div>
              {overlayButtons.map((button, index) => (
                <BranchButton
                  key={button.id}
                  button={button}
                  index={index}
                  compact={true}
                  variant="box"
                  defaultButtonColor={config.defaultButtonColor}
                  onSelect={(buttonId) => void playerApiRef.current?.selectButton(buttonId)}
                />
              ))}
            </div>
          )}

          {isDecisionVisible && choiceButtons.length === 0 && ctaUrl && (
            <InfoCTAButton
              ctaUrl={ctaUrl}
              ctaLabel={ctaLabel}
              onTrackCtaClick={onCtaClick}
              onPauseVideo={onPauseVideo}
            />
          )}
        </div>
)

export default BoxBottomBar
